import React from 'react';
import { useSearchParams } from 'react-router-dom';

// Jumping to a page is only half of "take me to this field" — the field itself
// is often far below the fold. Every entry point (progress checklist, profile
// Edit / Add buttons) appends `?field=<formik name>` to its route, and the
// destination page calls useScrollToField() to bring that field into view.
export const FIELD_PARAM = 'field';

// Appends the field marker to a route the caller already built.
export const routeToField = (route, field) => {
  if (!route || !field) return route;
  return `${route}${route.includes('?') ? '&' : '?'}${FIELD_PARAM}=${encodeURIComponent(
    field
  )}`;
};

// Wrappers carry `data-field` because the control that owns the name is often a
// hidden file input or a Quill editor with no matching DOM node to scroll to.
const findFieldElement = (field) =>
  document.querySelector(`[data-field="${field}"]`) ||
  document.querySelector(`[name="${field}"]`) ||
  document.getElementById(field);

// The target usually mounts after its data lands, so keep looking for a few
// seconds instead of giving up on the first frame.
export const scrollToField = (field, { attempts = 24, interval = 150 } = {}) => {
  if (!field || typeof document === 'undefined') return () => {};

  let cancelled = false;
  let tries = 0;
  let timer = null;

  const attempt = () => {
    if (cancelled) return;
    const element = findFieldElement(field);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    tries += 1;
    if (tries < attempts) timer = setTimeout(attempt, interval);
  };

  attempt();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
};

// `ready` lets a page hold the scroll until its form has data to render.
export const useScrollToField = (ready = true) => {
  const [searchParams] = useSearchParams();
  const field = searchParams.get(FIELD_PARAM);
  // Scrolling is a one-shot arrival effect: without this, any re-render would
  // yank the page back while the user is typing.
  const handledField = React.useRef(null);

  React.useEffect(() => {
    if (!field || !ready || handledField.current === field) return undefined;
    handledField.current = field;
    return scrollToField(field);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, ready]);
};

export default useScrollToField;
